import { useCallback, useEffect, useMemo, useState } from "react";
import PageRecord from "models/view-models/page-record";
import PageService, {
    PageResourcePathParams,
    UpdatePageAccessDetailsBodyParams,
} from "utilities/services/notebooks/page-service";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";

interface UseManagePageAccessDetails {
    clearErrors: () => void;
    errors: string[];
    isPubliclyAccessible: boolean;
    isCloningPermitted: boolean;
    publicPageLink: string;
    refreshPage: () => Promise<void>;
    loading: boolean;
    updatePageAccessDetails: (
        isPubliclyAccessible: boolean,
        isCloningPermitted: boolean
    ) => Promise<boolean>;
}

const ERROR_MESSAGE_DELIMITER: string = ";";

export const useManagePageAccessDetails = (
    pageId: number | null
): UseManagePageAccessDetails => {
    const { get: getPageQuery } = PageService.useGetQuery();
    const { update: updatePageAccessDetailsApi } =
        PageService.useUpdateAccessDetails();
    const [page, setPage] = useState<PageRecord>();
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const {
        data: pageResult,
        isFetching,
        refetch: refetchPage,
    } = getPageQuery({ id: pageId! }, undefined, {
        enabled: pageId != null && !Number.isNaN(pageId),
        staleTime: 60 * 1000, // 60 seconds
    });

    useEffect(() => {
        if (pageId == null) {
            setPage(undefined);
            return;
        }

        if (pageResult == null) {
            return;
        }

        if (pageResult.result == null || pageResult.resultObject == null) {
            setErrors([`Error retrieving page with ID ${pageId}`]);
            return;
        }

        if (pageResult.result.hasErrors()) {
            setErrors(pageResult.result.listErrorMessages());
            return;
        }

        setPage(pageResult.resultObject);
    }, [pageResult, pageId]);

    const loadPage = useCallback(async (): Promise<void> => {
        if (pageId == null) {
            setPage(undefined);
            return;
        }

        try {
            setLoading(true);

            const { data: getResponse } = await refetchPage();

            if (
                getResponse?.result == null ||
                getResponse?.resultObject == null
            ) {
                throw new Error(`Error retrieving page with ID ${pageId}`);
            }

            if (getResponse.result.hasErrors()) {
                throw new Error(
                    getResponse.result
                        .listErrorMessages()
                        .join(ERROR_MESSAGE_DELIMITER)
                );
            }

            setPage(getResponse.resultObject);
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : `Error retrieving page with ID ${pageId}`;

            setErrors(message.split(ERROR_MESSAGE_DELIMITER));
        } finally {
            setLoading(false);
        }
    }, [pageId, refetchPage]);

    const updatePageAccessDetails = useCallback(
        async (
            isPubliclyAccessible: boolean,
            isCloningPermitted: boolean
        ): Promise<boolean> => {
            if (pageId == null) {
                setLoading(false);
                return false;
            }

            try {
                setLoading(true);

                const pathParams: PageResourcePathParams = { id: pageId };
                const updateParams: UpdatePageAccessDetailsBodyParams = {
                    isPubliclyAccessible,
                    isCloningPermitted,
                };

                const updateResponse = await updatePageAccessDetailsApi(
                    updateParams,
                    pathParams
                );

                if (
                    updateResponse.result == null ||
                    updateResponse.resultObject == null
                ) {
                    throw Error;
                }

                if (updateResponse.result.hasErrors()) {
                    throw new Error(
                        updateResponse.result
                            .listErrorMessages()
                            .join(ERROR_MESSAGE_DELIMITER)
                    );
                }

                setPage(updateResponse.resultObject);

                return true;
            } catch (error) {
                const message =
                    error instanceof Error
                        ? error.message
                        : `Error updating page with ID ${pageId}`;

                setErrors(message.split(ERROR_MESSAGE_DELIMITER));

                return false;
            } finally {
                setLoading(false);
            }
        },
        [pageId, updatePageAccessDetailsApi]
    );

    const clearErrors = () => setErrors([]);

    const publicPageLink = useMemo(
        () =>
            window.location.origin +
            RouteUtils.getUrl(siteMap.public.sharedPages, {
                pageGuid: page?.sharingId ?? "",
            }),
        [page?.sharingId]
    );

    return {
        clearErrors,
        errors,
        isPubliclyAccessible: page?.isPubliclyAccessible ?? false,
        isCloningPermitted: page?.isCloningPermitted ?? false,
        publicPageLink,
        refreshPage: loadPage,
        loading: loading || isFetching,
        updatePageAccessDetails,
    };
};
